import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, Eyebrow } from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import Packages from "@/components/Packages";
import { PaperPlane } from "@/components/PaperPlane";

const services = [
  {
    title: "Brand Voice & ICP Strategy",
    forWho: "Parrish, Bradenton, Sarasota & Tampa area business owners who blend in when they should stand out.",
    included: ["Deep-dive identity & positioning session", "Ideal Client Profile (ICP) development", "Competitive differentiation", "Core message framework"],
    walk: "Total clarity on who you are, who you serve, and what makes you the only real choice.",
  },
  {
    title: "Ad Script Writing & Video Coaching",
    forWho: "Owners ready to be the face of their brand but unsure what to say or how to say it.",
    included: ["Conversion-focused ad scripts", "1:1 on-camera coaching session", "Delivery & confidence refinement", "Filming support and direction"],
    walk: "Authentic video creative that sounds and looks like you. We show up. We coach you. We help you be yourself on camera.",
  },
  {
    title: "Meta Ads Management",
    forWho: "Local businesses that want a real, optimized ad presence, not boosted posts.",
    included: ["Full campaign build & pixel setup", "Local targeting across Manatee, Sarasota, Hillsborough & Pinellas Counties", "CBO ad set structure", "Ongoing optimization & monthly reporting"],
    walk: "A managed, optimized Meta presence reaching the exact people in your area who need what you offer.",
  },
  {
    title: "Full-Service Local Market Package",
    forWho: "Business owners ready to become the most recognized name in their market.",
    included: ["Everything in Voice, Video & Ads", "End-to-end strategy & production", "Priority optimization", "Quarterly positioning reviews"],
    walk: "Our flagship engagement, the complete system to dominate your local market.",
  },
];

const faqs = [
  { q: "I've tried Facebook Ads before and it didn't work.", a: "Most failed campaigns share one of three problems: they ran engagement campaigns instead of conversion campaigns, they didn't test hooks before committing to video, or the messaging was built around what the business wanted to say instead of what stops the scroll for the buyer. We address all three from day one. Same platform, completely different strategy." },
  { q: "Why do I need 9, 18, or 36 ads? Can't one good ad just work?", a: "It can, for a while. Then your audience sees it enough times that they stop registering it. Ad fatigue is the thing nobody talks about until cost-per-lead spikes in month two. Running creative in sets keeps fresh ads rotating in before performance drops, so your cost-per-lead stays consistent instead of spiking and crashing." },
  { q: "I'm not comfortable on camera.", a: "Nobody is, until they've done it with the right coaching. The goal isn't polished, it's believable. People hire businesses they trust, and trust comes from a real person talking directly to them. On-camera coaching is built into every package for exactly this reason." },
  { q: "Why does ad spend go to Meta directly, not through you?", a: "Because the data belongs to you. Pixel data, audience data, campaign history, these are real business assets. When ad spend runs through someone else's account, that data can disappear when the relationship ends. With us, your account, and everything it learns, always stays with you." },
  { q: "Do you work with businesses outside Florida?", a: "Yes. The system runs nationally. The Meta strategy, the CRM build, and the automation sequences are all location-agnostic, and we coordinate the shoot wherever your business operates. Your market is wherever your best customers are." },
  { q: "What makes your approach different?", a: "A lot of marketing sells activity, posts, reports, boosted content. We build a conversion system where every component is built to produce a booked customer, not a metric. And you own the asset when it's done: the pixel, the audiences, the creative, and the CRM are all yours." },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Meta Ads Management Services | Local Businesses in Tampa Bay | Prolific Maven"
        description="Brand voice strategy, ad video coaching & full-service Meta ads management for local businesses in Bradenton, Sarasota & Tampa. Apply to work with Prolific Maven."
        path="/services"
      />
      <PageHero
        eyebrow="Services"
        title="Everything you need to go from invisible to unforgettable, in your own backyard."
      />

      <section className="relative z-10 bg-ink-800 border-t border-white/10" data-testid="services-list">
        {services.map((s, i) => (
          <Reveal key={s.title} className="border-b border-white/10">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="font-display font-black text-gold/30 text-6xl">0{i + 1}</span>
                <h2 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight flex items-start gap-3">
                  <PaperPlane className="w-6 h-6 mt-1 shrink-0" />{s.title}
                </h2>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-gold mb-3">Who it's for</h3>
                  <p className="text-cream/70 leading-relaxed">{s.forWho}</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-gold mb-3">What's included</h3>
                  <ul className="space-y-2">
                    {s.included.map((x) => <li key={x} className="text-cream/70 text-sm leading-relaxed">, {x}</li>)}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-gold mb-3">What you walk away with</h3>
                  <p className="text-cream/70 leading-relaxed">{s.walk}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <Packages />

      <section className="relative z-10 bg-ink-800 border-t border-white/10 py-24 sm:py-32" data-testid="services-faq">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>FAQ</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white">Questions, answered.</h2>
          </Reveal>
          <Reveal delay={140}>
            <Accordion type="single" collapsible className="mt-12">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10" data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display font-semibold text-lg sm:text-xl text-white hover:text-gold hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-cream/70 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
